import { booleanInteger, OptionItem } from "blu-utilities";
import { ItemStatus } from "src/app/core/models/general";
import { AuthorizationService } from "src/app/core/services/authorization.service";
import { PermissionNames } from "src/models/modules";
import { PaginatedRequest } from "src/models/requests";

export interface GetTicketsRequest extends PaginatedRequest {
  customer_type?: CustomerType;
  "statuses[]"?: TicketStatus[];
  partner_assignee_id?: string;
  "partner_ids[]"?: number[];
  type_id?: string;
  search?: string;
}

export enum CustomerType {
  MEMBER = "Member",
  PARTNER = "Partner",
}

export enum DigitalSurveySubscriptionOptions {
  NONE = "none",
  EMAIL = "email",
  SMS = "sms",
  EMAIL_AND_SMS = "email_and_sms",
}

export enum TicketStatus {
  IN_PROGRESS = "In Progress",
  CLOSED = "Closed",
  ESCALATED = "Escalated",
}

export enum TicketStatusLabels {
  IN_PROGRESS = "In Progress",
  CLOSED = "Closed",
  ESCALATED = "Escalated",
}

export function getTicketStatusMap(): Map<TicketStatus, TicketStatusLabels> {
  return new Map([
    [TicketStatus.IN_PROGRESS, TicketStatusLabels.IN_PROGRESS],
    [TicketStatus.CLOSED, TicketStatusLabels.CLOSED],
    [TicketStatus.ESCALATED, TicketStatusLabels.ESCALATED],
  ]);
}

export interface CreateTicketRequest {
  status: TicketStatus;
  customer_type?: CustomerType;
  type_id?: string;
  partner_assignee_id?: string;
  partner_id?: string;
  member_id?: string;
  ticket: string;
}

export interface Ticket {
  id: number;
  status: TicketStatus;
  customerType: string;
  assigneeName: string;
  partnerName: string;
  identity: string;
  adminName: string;
  createdAt: Date | null;
}

export interface TicketDetails {
  createdAt: Date | null;
  customerType: CustomerType;
  id: number;
  memberId: number;
  memberReference: string;
  partnerAssigneeId: number;
  partnerId: number;
  status: TicketStatus;
  ticket: string;
  adminName: string;
  typeId: number;
}

export function getTicketStatusesOptions(): OptionItem[] {
  const result: OptionItem[] = [];
  getTicketStatusMap()?.forEach((label, value) => {
    result.push({
      label,
      value,
    });
  });
  return result;
}

export function getCustomerTypesOptions(): OptionItem[] {
  return [
    { label: CustomerType.MEMBER, value: CustomerType.MEMBER },
    { label: CustomerType.PARTNER, value: CustomerType.PARTNER },
  ];
}

export function getEarnedBadgesStatusOptions(): OptionItem[] {
  return [
    { label: "Active", value: ItemStatus.ACTIVE },
    { label: "Archived", value: ItemStatus.INACTIVE },
  ];
}

export const ActionsTransactionCanBeReversed = [1, 2];

export class CallCenterPermissions {
  private authorizationService: AuthorizationService;
  constructor(authorizationService: AuthorizationService) {
    this.authorizationService = authorizationService;
  }
  canUpdateAffiliationStatus(): boolean {
    return this.authorizationService.can(
      PermissionNames.CALL_CENTER_UPDATE_STATUS,
    );
  }

  canRewardPoints(): boolean {
    return this.authorizationService.can(PermissionNames.REWARD_POINTS);
  }

  canResetPassword() {
    return this.authorizationService.can(
      PermissionNames.REQUIRE_PASSWORD_RESET,
    );
  }

  canAddAffiliateUserIdentifiers() {
    return this.authorizationService.can(
      PermissionNames.ADD_AFFILIATE_USER_IDENTIFIERS,
    );
  }

  canRewardPointsByRule(): boolean {
    return this.authorizationService.can(PermissionNames.REWARD_BY_RULE);
  }

  canRewardAccorPointsManually() {
    return this.authorizationService.can(
      PermissionNames.REWARD_ACCOR_POINTS_MANUALLY,
    );
  }

  canRedeemPoints(): boolean {
    return this.authorizationService.can(PermissionNames.REDEEM_POINTS);
  }

  canRedeemItems(): boolean {
    return this.authorizationService.can(PermissionNames.REDEEM_ITEMS);
  }

  canReSendPin(): boolean {
    return this.authorizationService.can(PermissionNames.RESEND_PIN);
  }

  canUpdateMemberShipTier(): boolean {
    const permissions = [
      PermissionNames.EDIT_REGULAR_MEMBERSHIP_TIER,
      PermissionNames.EDIT_HOME_MEMBERSHIP_TIER,
    ];

    return permissions.some((permission) =>
      this.authorizationService.can(permission),
    );
  }

  canUpdateProfile(): boolean {
    return this.authorizationService.can(PermissionNames.EDIT_PROFILES);
  }

  canSendPasswordVerificationLink(): boolean {
    return this.authorizationService.can(
      PermissionNames.SEND_PASSWORD_RESET_VERIFICATION_LINK,
    );
  }

  canViewPostedTransactions(): boolean {
    return this.authorizationService.can(
      PermissionNames.VIEW_POSTED_TRANSACTIONS,
    );
  }

  canViewOrders(): boolean {
    return this.authorizationService.can(PermissionNames.VIEW_ORDERS);
  }

  canViewHospitalityTransactions(): boolean {
    return this.authorizationService.can(
      PermissionNames.VIEW_HOSPITALITY_TRANSACTIONS,
    );
  }
  canViewPendingNegativeRewardTransactions(): boolean {
    return this.authorizationService.can(
      PermissionNames.VIEW_PENDING_NEGATIVE_REWARD_TRANSACTIONS,
    );
  }

  canViewCloCashback(): boolean {
    return this.authorizationService.can(PermissionNames.VIEW_CLO_CASHBACK);
  }

  canReverseRewardTransactions(): boolean {
    return this.authorizationService.can(
      PermissionNames.REVERSE_REWARD_TRANSACTION,
    );
  }

  canReverseRedemptionTransactions(): boolean {
    return this.authorizationService.can(
      PermissionNames.REVERSE_REDEMPTION_TRANSACTION,
    );
  }

  canReverseMerchantAppTransactions(): boolean {
    return this.authorizationService.can(
      PermissionNames.REVERSE_MERCHANT_APP_TRANSACTION,
    );
  }

  canEditRegularMembershipTier() {
    return this.authorizationService.can(
      PermissionNames.EDIT_REGULAR_MEMBERSHIP_TIER,
    );
  }

  canEditHomeMembershipTier() {
    return this.authorizationService.can(
      PermissionNames.EDIT_HOME_MEMBERSHIP_TIER,
    );
  }

  canSendWelcomeEmail() {
    return this.authorizationService.can(PermissionNames.SEND_WELCOME_EMAIL);
  }

  canCreateSsoToken() {
    return this.authorizationService.can(PermissionNames.CREATE_SSO_TOKEN);
  }
}

export enum CallCenterTabs {
  CLO_TRANSACTIONS = "CLO Cashback Redemption",
  POSTED_TRANSACTIONS = "Posted Transactions",
  ORDERS = "Orders",
  HOSPITALITY_TRANSACTIONS = "Hospitality Transactions",
  PENDING_NEGATIVE_TRANSACTIONS = "Pending Negative Reward Transactions",
}

export const CANCEL_MY_MEMBERSHIP_TICKET_TYPE = "Cancel my membership";
export const BLU_POINTS_PARTNER_ID = 1;

export interface TicketDetailsInfo {
  memberId: number;
  partnerId: number;
}

export interface TicketActionOptions {
  hasPermissionToView: boolean;
  isSelected: boolean;
  dataAutoId: string;
  label: string;
  submitted: () => void;
}

export interface CreateAffiliateUserIdentifierRequest {
  partner_id?: number;
  affiliate_program_id?: number;
  is_preferred?: number;
  identifier_value?: number;
  first_name?: number;
  last_name?: number;
  email?: number;
  mobile_number?: number;
}

export interface AffiliateUserIdentifier {
  id: number;
  partnerId: number;
  partnerName: string;
  affiliateProgramId: number;
  affiliateProgramName: string;
  isPreferred: boolean;
  identifierValue: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
}

export interface GetEarnedBadgeRequest extends PaginatedRequest {
  partner_id: number;
  search_phrase?: string;
  is_active?: booleanInteger;
}

export interface GetTotalEarnedBadgeRequest {
  partner_id: number;
  is_active?: booleanInteger;
}

export interface EarnedBadge {
  memberId: number;
  triggerCampaignId: number;
  triggerItemId: number;
  triggerActionExecutionId: number;
  badgeName: string;
  collectionName: string;
  isActive: boolean;
  createdAt: Date | null;
}

export interface TotalEarnedBadges {
  totalEarnedBadges: number;
  totalActiveEarnedBadges: number;
  lastEarnedBadgeDate: Date | null;
}

export function getDigitalSurveySubscriptionOptions(): OptionItem<DigitalSurveySubscriptionOptions>[] {
  return [
    {
      label: "None",
      value: DigitalSurveySubscriptionOptions.NONE,
    },
    {
      label: "Email",
      value: DigitalSurveySubscriptionOptions.EMAIL,
    },
    {
      label: "SMS",
      value: DigitalSurveySubscriptionOptions.SMS,
    },
    {
      label: "Email & SMS",
      value: DigitalSurveySubscriptionOptions.EMAIL_AND_SMS,
    },
  ];
}
